// src/app/services/geolocation.service.ts
import { Injectable, inject } from '@angular/core';
import { AuthService } from './auth.service';
import { FirestoreService } from './firestore.service';
import { GeoPoint, Timestamp } from '@angular/fire/firestore';
import { calcularDistancia } from '../utils/tab-id';
import { UserService } from './user.service';
import { GeocodingService, LocationInfo } from './geocoding.service';

@Injectable({ providedIn: 'root' })
export class GeolocationService {
  private watchId: number | null = null;
  private lastUpdateTime = 0;
  private readonly MIN_UPDATE_INTERVAL_MS = 60 * 60 * 1000; // 1 hora

  private readonly authService      = inject(AuthService);
  private readonly firestoreService = inject(FirestoreService);
  private readonly userService      = inject(UserService);
  private readonly geocoding        = inject(GeocodingService);

  // Señal reactiva con el usuario actual
  public user = this.userService.usuarioSignal();

  private readonly UMBRAL_DISTANCIA_KM = 100;

  public get lastKnownCoords(): { lat: number, lng: number } | null {
    const u = this.user;
    return u?.ubicacion
      ? { lat: u.ubicacion.latitude, lng: u.ubicacion.longitude }
      : null;
  }

  startTracking(): void {
    if (!navigator.geolocation) {
      return;
    }
    this.watchId = navigator.geolocation.watchPosition(
      pos => this.handlePosition(pos),
      err => console.error('[📍] Error obteniendo ubicación', err),
      {
        enableHighAccuracy: true,
        maximumAge: 60_000,   // acepta lecturas de hasta 60 s
        timeout:      10_000  // falla si no hay lectura en 10 s
      }
    );
  }

  stopTracking(): void {
    if (this.watchId !== null) {
      navigator.geolocation.clearWatch(this.watchId);
      this.watchId = null;
    }
  }

  private async handlePosition(position: GeolocationPosition) {
    const now = Date.now();
    if (now - this.lastUpdateTime < this.MIN_UPDATE_INTERVAL_MS) {
      // No pasa una hora desde la última actualización
      return;
    }
    this.lastUpdateTime = now;

    const lat = position.coords.latitude;
    const lng = position.coords.longitude;
    const ts  = Timestamp.now();

    const u = this.user;
    const prevPoint = u?.ubicacion
      ? { lat: u.ubicacion.latitude, lng: u.ubicacion.longitude }
      : null;

    const hasCityCountry = !!(u?.ciudad && u?.pais);
    const distKm = prevPoint
      ? calcularDistancia(prevPoint, { lat, lng })
      : null;

    // Solo actualizo si: no había ubicación, o no había ciudad/pais, o movido > umbral
    if (!prevPoint || !hasCityCountry || (distKm !== null && distKm >= this.UMBRAL_DISTANCIA_KM)) {
      await this.updateFirestore(lat, lng, ts, hasCityCountry);
    }
  }

  /**
   * Actualiza usuario en Firestore con nueva ubicación (+ ciudad/pais si aplica).
   */
  private async updateFirestore(
    lat: number,
    lng: number,
    ts: Timestamp,
    hadCityCountry: boolean
  ): Promise<void> {
    const uid = this.authService.uid;
    if (!uid) {
      console.warn('[📍] No autenticado, no actualizo Firestore.');
      return;
    }

    try {
      let city: string|undefined, country: string|undefined;
      if (!hadCityCountry) {
        try {
          const info: LocationInfo = await this.geocoding.reverseGeocode(lat, lng);
          city    = info.city;
          country = info.country;
        } catch (err) {
          console.warn('[📍] Geocoding falló:', err);
        }
      }

      const data: any = {
        ubicacion: new GeoPoint(lat, lng),
        actualizadoEn: ts
      };
      if (city)    data.ciudad  = city;
      if (country) data.pais    = country;

      await this.firestoreService.updateDoc('usuarios', uid, data);
    } catch (err) {
      console.error(`[📍] Error actualizando usuarios/${uid}:`, err);
    }
  }
}
